import { SxProps, Theme } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import makeStyles from "@mui/styles/makeStyles";
import { TourElement } from "core/types";
import {
  GeneralTableProps,
  StyledInnerCellWrapper,
} from "ds_legacy/components/Tables/GeneralTable";
import {
  TABLE_HOVER_GREY,
  TABLE_SELECTED,
  TABLE_SELECTED_HOVER,
} from "ds_legacy/materials/colors";
import { margin, sizing } from "ds_legacy/materials/metrics";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import {
  COLUMN_TYPE_DEFAULT_ACTION,
  COLUMN_TYPE_DEFAULT_STRING as DEFAULT_COLUMN_TYPE,
} from "./columnTypeConstants";
import {
  ColumnKinds,
  GeneralTableColumn,
  getCellComponent,
} from "./CustomCells";
import { ActionMenu, GeneralListActionKind } from "./MenuActions";

const useStyles = makeStyles(() => ({
  hover: {
    "&:hover": {
      backgroundColor: TABLE_HOVER_GREY,
      cursor: "pointer",
    },
    "&.Mui-selected:hover": {
      backgroundColor: TABLE_SELECTED_HOVER,
    },
  },
  /* Styles applied to the root element. */
  root: {
    "&.Mui-selected": {
      backgroundColor: TABLE_SELECTED,
    },
    "&.Mui-selected:hover": {
      backgroundColor: TABLE_SELECTED,
    },
    // Default root styles
    color: "inherit",
    display: "table-row",
    // Disabling focus ring for mouse, touch and keyboard users.
    outline: 0,
    verticalAlign: "middle",
  },
}));

export function convertAlignToJustifyContent<Data, Kind extends ColumnKinds>(
  align: GeneralTableColumn<Data, Kind>["align"],
): CSSProperties["justifyContent"] {
  return align === "right" ? "flex-end" : "flex-start";
}

async function getCells<Data>({
  columns,
  rowData,
}: {
  columns: GeneralTableColumn<NonNullable<Data>, ColumnKinds>[];
  rowData: Data;
}) {
  const columnPromises = columns.map(
    async (
      {
        align,
        disableDivider,
        getProps,
        title,
        type = DEFAULT_COLUMN_TYPE,
        width,
      },
      index,
    ) => {
      const Component = getCellComponent({ type });
      const props =
        typeof getProps === "function"
          ? await getProps(rowData as NonNullable<Data>)
          : { [getProps]: rowData[getProps as keyof Data] };

      disableDivider =
        index === 0 || typeof title !== "string" || disableDivider;
      return (
        <TableCell
          key={index}
          align={align}
          data-type={type}
          sx={{
            marginLeft: index !== 0 ? margin(0.5) : undefined,
            width,
          }}
        >
          <StyledInnerCellWrapper
            aligned
            boxSizing="border-box"
            disableDivider={disableDivider}
            isTransparentDivider
            justify={convertAlignToJustifyContent(align)}
            width="100%"
          >
            <Component {...(props as any)} />
          </StyledInnerCellWrapper>
        </TableCell>
      );
    },
  );
  const result = await Promise.all(columnPromises);
  return result;
}

export function useGetTableBody<
  Data extends { id: number | string },
  RowActions extends GeneralListActionKind[],
>({
  actions,
  columns,
  data,
  dependencies = [],
  getA11yRowProps,
  getRowStyle,
  getRowTestId,
  id,
  isSelected,
  onRowClick,
  rowClickDisabled,
  tour,
}: Pick<
  GeneralTableProps<Data, RowActions>,
  | "actions"
  | "columns"
  | "data"
  | "dependencies"
  | "getA11yRowProps"
  | "getRowTestId"
  | "id"
  | "isSelected"
  | "onRowClick"
  | "rowClickDisabled"
  | "getRowStyle"
> & {
  tour?: TourElement;
}) {
  const classes = useStyles();
  const visibleColumns = columns.filter(({ isHidden }) => !isHidden);
  const [tableBody, setTableBody] = useState<React.JSX.Element[] | null>(null);
  const runIdRef = useRef(0);

  async function getTableBody() {
    if (!data) return null;

    const rowPromises = data.map(async (rowData, index) => {
      const disabledRowClick = rowClickDisabled?.({ data: rowData, index });
      const rowClickable = onRowClick && !disabledRowClick;
      const ariaProps = getA11yRowProps?.({ data: rowData, index }) || {};
      const selected = isSelected?.(rowData.id);
      const customRowStyle = getRowStyle?.({
        data: rowData,
        index,
      }) as SxProps<Theme>;

      return (
        <TableRow
          key={index}
          aria-checked={isSelected ? !!selected : undefined}
          aria-disabled={onRowClick ? disabledRowClick : undefined}
          classes={classes}
          data-testid={
            getRowTestId ? getRowTestId(rowData) : `${id}-row-${rowData.id}`
          }
          hover={rowClickable}
          selected={selected}
          sx={customRowStyle}
          tabIndex={0}
          onClick={(event) => {
            if (disabledRowClick) return;
            onRowClick?.({ event, rowData });
          }}
          {...ariaProps}
          {...tour}
        >
          {await getCells({ columns: visibleColumns, rowData })}
          {actions ? (
            <TableCell
              align="right"
              data-type={COLUMN_TYPE_DEFAULT_ACTION}
              style={{
                marginLeft: margin(0.5),
                overflow: "visible",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: actions.width ?? sizing(4),
              }}
              onClick={(e) => e.stopPropagation()}
              {...actions.tour?.(index)}
            >
              {actions.renderCustomAction ? (
                actions.renderCustomAction(rowData)
              ) : (
                <ActionMenu<Data, RowActions> {...actions} data={rowData} />
              )}
            </TableCell>
          ) : null}
        </TableRow>
      );
    });

    const result = await Promise.all(rowPromises);
    return result;
  }

  useEffect(() => {
    const thisRun = (runIdRef.current += 1);
    (async () => {
      try {
        const body = await getTableBody();
        if (thisRun === runIdRef.current) {
          setTableBody(body);
        }
      } catch (err) {
        if (thisRun === runIdRef.current) {
          console.error(err);
        }
      }
    })();
  }, [data, ...dependencies]);

  return { tableBody, visibleColumns };
}
